import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import {
  Plus,
  Settings,
  LogOut,
  User,
  Grid3X3,
  List,
  CreditCard,
  Menu,
  LayoutDashboard,
  Package,
  FolderOpen,
  Search,
  FileText,
  Users,
  ChevronRight,
  Bell,
  HelpCircle,
  Download
} from 'lucide-react';
import { NotificationDropdown } from './NotificationDropdown';
import { ThemeToggle } from './theme-toggle';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';
import { ApplicationAPI } from '@/api/applications';
import { ProjectAPI } from '@/api';
import { ScanAPI } from '@/api/scans';
import { isSubscriptionValid } from '@/helpers/common';
import { SubscriptionDropdown } from './SubscriptionDropdown';
import { WorkgroupSwitcher } from './WorkgroupSwitcher';

interface NavigationProps {
  currentRoute: string;
  isGridLayout: boolean;
  onLayoutToggle: () => void;
  onNewScan: () => void;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  onApplicationSelect: (applicationId: number) => void;
  profileImageUrl?: string;
  routeParams?: any;
  searchParams?: any;
}

// `requiredPermission` gates the nav item on the active workgroup's resolved
// role permissions (see AuthContext.hasPermission); omit it for items every
// workgroup member should see regardless of role.
const allNavigationItems = [
  { icon: LayoutDashboard, label: "Dashboard", route: "dashboard" },
  { icon: Package, label: "Applications", route: "applications", requiredPermission: { module: 'applications', action: 'read' } },
  { icon: FolderOpen, label: "Projects", route: "projects", requiredPermission: { module: 'projects', action: 'read' } },
  { icon: Search, label: "Scans", route: "scans", requiredPermission: { module: 'scans', action: 'read' } },
  { icon: FileText, label: "Components Hub", route: "components-hub", requiredPermission: { module: 'components', action: 'read' } },
  { icon: Bell, label: "Notifications", route: "notifications" },
  { icon: Users, label: "Team", route: "team", requiredPermission: { module: 'teams', action: 'read' } },
];

const getPageInfo = async (route: string, routeParams?: any,searchParams?: any) => {
  switch (route) {
    case 'dashboard':
      return { title: 'Dashboard', subtitle: 'Your Operational Insights' };
    case 'applications':
      return { title: 'Applications', subtitle: 'Manage your application portfolio' };
    case 'projects':
      if (routeParams?.applicationId) {
        try {
          const response = await ApplicationAPI.getById(routeParams.applicationId);
          if (response.data) {
            return {
              title: 'Projects',
              subtitle: `Manage projects for ${response.data.name}`,
              breadcrumbs: [
                { label: response.data.name, route: `applications` },
                { label: 'Projects', route: null }
              ]
            };
          }
        } catch (error) {
        }
      }
      return { title: 'Projects', subtitle: 'Manage your assessment projects' };
    case 'new-project':
      return { title: 'New Project', subtitle: 'Create a new assessment project' };
    case 'scans':
      if (routeParams?.projectId) {
        try {
          const response = await ProjectAPI.getById(routeParams.projectId);
          if (response.data) {
            let applicationName = 'Unknown Application';
            if (response.data.applicationid) {
              try {
                const appResponse = await ApplicationAPI.getById(response.data.applicationid);
                if (appResponse.data) {
                  applicationName = appResponse.data.name;
                }
              } catch (appError) {
              }
            }

            return {
              title: 'Scans',
              subtitle: `Manage scans for ${response.data.name}`,
              breadcrumbs: [
                { label: applicationName, route: `applications` },
                { label: response.data.name, route: `projects/${response.data.applicationid}` },
                { label: 'Scans', route: null }
              ]
            };
          }
        } catch (error) {
        }
      } else  if (searchParams?.get('applicationId')) {
        try {
          const response = await ApplicationAPI.getById(searchParams.get('applicationId'));
          if (response.data) {
            return {
              title: 'Scans',
              subtitle: `Manage scans for ${response.data.name}`,
              breadcrumbs: [
                { label: response.data.name, route: `applications` },
                { label: 'Scans', route: null }
              ]
            };
          }
        } catch (error) {
        }
      }
      return { title: 'Scans', subtitle: 'Monitor and manage scans' };
    case 'team':
      return { title: 'Team', subtitle: 'Manage team members and permissions' };
    case 'profile':
      return { title: 'Profile', subtitle: 'Manage your personal information and preferences' };
    case 'settings':
      return { title: 'Settings', subtitle: 'Manage your account and preferences' };
    case 'billing':
      return { title: 'Billing', subtitle: 'Manage your subscription and billing information' };
    case 'application-view-report':
      if (routeParams?.applicationId) {
        try {
          const response = await ApplicationAPI.getById(routeParams.applicationId);
          if (response.data) {
            return {
              title: 'Operational Insights',
              subtitle: 'Comprehensive analysis and reporting',
              breadcrumbs: [
                { label: response.data.name, route: `applications` },
                { label: 'Operational Insights', route: null }
              ]
            };
          }
        } catch (error) {
        }
      }
      return { title: 'Operational Insights', subtitle: 'Comprehensive analysis and reporting' };
    case 'project-view-report':
      if (routeParams?.projectId) {
        try {
          const response = await ProjectAPI.getById(routeParams.projectId);
          if (response.data) {
            let applicationName = 'Unknown Application';
            if (response.data.applicationid) {
              try {
                const appResponse = await ApplicationAPI.getById(response.data.applicationid);
                if (appResponse.data) {
                  applicationName = appResponse.data.name;
                }
              } catch (appError) {
              }
            }

            return {
              title: 'Operational Insights',
              subtitle: 'Comprehensive analysis and reporting',
              breadcrumbs: [
                { label: applicationName, route: `applications` },
                { label: response.data.name, route: `projects/${response.data.applicationid}` },
                { label: 'Scan Summary', route: null }
              ]
            };
          }
        } catch (error) {
        }
      }
      return { title: 'Operational Insights', subtitle: 'Comprehensive analysis and reporting' };
    case 'component-view-report':
      if (routeParams?.scanId) {
        try {
          const response = await ScanAPI.getById(routeParams.scanId);
          if (response.data) {
            const scan = response.data;

            let projectName = 'Unknown Project';
            let applicationName = 'Unknown Application';
            let applicationId = null;

            if (scan.projectid) {
              try {
                const projectResponse = await ProjectAPI.getById(scan.projectid);
                if (projectResponse.data) {
                  projectName = projectResponse.data.name;
                  applicationId = projectResponse.data.applicationid;

                  if (applicationId) {
                    try {
                      const appResponse = await ApplicationAPI.getById(applicationId);
                      if (appResponse.data) {
                        applicationName = appResponse.data.name;
                      }
                    } catch (appError) {
                    }
                  }
                }
              } catch (projectError) {
              }
            }

            return {
              title: 'Operational Insights',
              subtitle: 'Comprehensive analysis and reporting',
              breadcrumbs: [
                { label: applicationName, route: `applications` },
                { label: projectName, route: `projects/${applicationId}` },
                { label: `${scan.version || ''}`, route: `scans/${scan.projectid}` },
                { label: 'Scan Summary', route: null }
              ]
            };
          }
        } catch (error) {
        }
      }
      return { title: 'Operational Insights', subtitle: 'Comprehensive analysis and reporting' };
    case 'components-hub':
       if (searchParams?.get('projectId')) {
        try {
          const response = await ProjectAPI.getById(searchParams.get('projectId'));
          if (response.data) {
            let applicationName = 'Unknown Application';
            if (response.data.applicationid) {
              try {
                const appResponse = await ApplicationAPI.getById(response.data.applicationid);
                if (appResponse.data) {
                  applicationName = appResponse.data.name;
                }
              } catch (appError) {
              }
            }

            return {
              title: 'Components Hub',
              subtitle: `Manage components for ${response.data.name}`,
              breadcrumbs: [
                { label: applicationName, route: `applications` },
                { label: response.data.name, route: `projects/${response.data.applicationid}` },
                { label: 'Components Hub', route: null }
              ]
            };
          }
        } catch (error) {
        }
      } else  if (searchParams?.get('applicationId')) {
        try {
          const response = await ApplicationAPI.getById(searchParams.get('applicationId'));
          if (response.data) {
            return {
              title: 'Components Hub',
              subtitle: `Manage components for ${response.data.name}`,
              breadcrumbs: [
                { label: response.data.name, route: `applications` },
                { label: 'Components Hub', route: null }
              ]
            };
          }
        } catch (error) {
        }
      }
      return { title: 'Components Hub', subtitle: 'Manage your components' };
    case 'notifications':
      return { title: 'Notifications', subtitle: 'View and manage your notifications' };
    case 'help':
      return { title: 'Help & Support', subtitle: 'Access documentation and support resources' };
    case 'third-party-list':
      return { title: 'Orbit8 Application Third-party component List', subtitle: 'Review third-party components and license obligations' };
    case 'terms-of-service':
      return { title: 'Terms of Service', subtitle: 'Review our terms and conditions' };
    case 'privacy-policy':
      return { title: 'Privacy Policy', subtitle: 'Understand how we handle your data' };
    case 'gdpr-policy':
      return { title: 'GDPR Policy', subtitle: 'Learn about our GDPR practices' };
    default:
      return { title: 'Dashboard', subtitle: 'Your Operational Insights' };
  }
};

export function Navigation({
  currentRoute,
  isGridLayout,
  onLayoutToggle,
  onNewScan,
  onNavigate,
  onLogout,
  profileImageUrl,
  routeParams,
  searchParams
}: NavigationProps) {
  const [pageInfo, setPageInfo] = useState<{
    title: string;
    subtitle: string;
    breadcrumbs?: Array<{ label: string; route: string | null }>
  }>({ title: 'Dashboard', subtitle: 'Your Operational Insights' });

  const { user, subscription, hasPermission } = useAuth();

  useEffect(() => {
    const fetchPageInfo = async () => {
      const info = await getPageInfo(currentRoute, routeParams,searchParams);
      setPageInfo(info);
    };
    fetchPageInfo();
  }, [currentRoute, routeParams,searchParams]);
  const getInitials = (name: string = 'User') => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  const renderBreadcrumbs = (breadcrumbs: Array<{ label: string; route: string | null }>) => {
    return (
      <div className="flex items-center space-x-1 text-xs sm:text-sm">
        {breadcrumbs.map((crumb, index) => (
          <div key={index} className="flex items-center space-x-1">
            {crumb.route ? (
              <button
                onClick={() => onNavigate(crumb.route!)}
                className="text-muted-foreground hover:text-foreground transition-colors duration-200 truncate max-w-32"
                title={crumb.label}
              >
                {crumb.label}
              </button>
            ) : (
              <span className="text-foreground font-medium truncate max-w-32" title={crumb.label}>
                {crumb.label}
              </span>
            )}
            {index < breadcrumbs.length - 1 && (
              <ChevronRight className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>
    );
  };

  const initials = getInitials(user.firstname + ' ' + user.lastname);

  const navigationItems = allNavigationItems.filter(
    item => !item.requiredPermission || hasPermission(item.requiredPermission.module, item.requiredPermission.action)
  );

  return (
    <div className="h-18 bg-background flex items-center justify-between px-4 border-b border-border min-w-0 w-full">
      <div className="flex items-center space-x-3 min-w-0 flex-shrink">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="sm:hidden p-2"
            >
              <Menu className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 bg-popover shadow-lg rounded-lg border border-border" align="start">
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = currentRoute === item.route;
              return (
                <DropdownMenuItem
                  key={item.route}
                  onClick={() => onNavigate(item.route)}
                  className={`flex items-center space-x-3 px-3 py-2 ${isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-md"
                      : "text-sidebar-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent hover:shadow-md"
                    }`}
                >
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${isActive
                      ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-lg shadow-sidebar-primary/20"
                      : "text-sidebar-foreground"
                    }`}>
                    <IconComponent className="w-4 h-4" />
                  </div>
                  <span className="font-medium">{item.label}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold text-foreground truncate">{pageInfo.title}</h1>
          {pageInfo.breadcrumbs ? (
            renderBreadcrumbs(pageInfo.breadcrumbs)
          ) : (
            <p className="text-xs sm:text-sm text-muted-foreground truncate">{pageInfo.subtitle}</p>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
        {(currentRoute === 'applications' || currentRoute === 'projects' || currentRoute === 'team' || currentRoute === 'scans') && (
          <div className="hidden md:flex items-center bg-card rounded-lg shadow-sm border border-border p-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onLayoutToggle}
              className={`h-10 px-3 ${!isGridLayout ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLayoutToggle}
              className={`h-10 px-3 ${isGridLayout ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
          </div>
        )}

        {!['new-project', 'profile', 'settings', 'billing', 'ssc-report'].includes(currentRoute) && (
          <Button
            variant="outline"
            onClick={() => onNavigate('download-scanner')}
            className="hidden sm:flex items-center gap-2"
            title="On-premise component analysis for codebase"
          >
            <Download className="w-4 h-4" />
            <span>Download Scanner</span>
          </Button>
        )}

        {!['new-project', 'profile', 'settings', 'billing', 'ssc-report'].includes(currentRoute) && hasPermission('scans', 'create') && isSubscriptionValid(subscription?.validupto || null) && (
          <Button
            onClick={onNewScan}
            className="hidden sm:flex shadow-lg shadow-primary/20 items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>New Scan</span>
          </Button>
        )}

        <WorkgroupSwitcher className="hidden sm:flex" />
        <ThemeToggle />
        <NotificationDropdown />
        <SubscriptionDropdown />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="p-0 h-auto shadow-sm">
              <Avatar className="w-10 h-10">
                {profileImageUrl && (
                  <AvatarImage
                    key={profileImageUrl}
                    src={import.meta.env.VITE_SOCKET_URL + profileImageUrl}
                    alt="User"
                  />
                )}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-48 bg-popover shadow-lg rounded-lg border border-border">
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onNavigate('profile');
              }}
              className="hover:bg-accent hover:text-accent-foreground"
            >
              <User className="w-4 h-4 mr-2" />
              Profile
            </DropdownMenuItem>
            {hasPermission('billing', 'read') && (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  onNavigate('billing');
                }}
                className="hover:bg-accent hover:text-accent-foreground"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Billing
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onNavigate('settings');
              }}
              className="hover:bg-accent hover:text-accent-foreground"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onNavigate('help');
              }}
              className="hover:bg-accent hover:text-accent-foreground"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Help
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onNavigate('third-party-list');
              }}
              className="hover:bg-accent hover:text-accent-foreground"
            >
              <FileText className="w-4 h-4 mr-2" />
              Third party List
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onLogout();
              }}
              className="hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}